import { Stack, TableCell, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import navigation from '../../../config/navigation'
import {
  getCurrentUser,
  getIsAdmin,
  getIsLeader,
  getIsLeaderOrManagement,
  getIsManagement,
} from '../../../store/redux/reducers/currentUserSlice'
import { EmployeeVacation, Vacation, VacationAcknowledgement } from '../../../store/types/utils/VacationTypes'
import { getDaysOfTheMonth } from '../../../store/utils/worklogUtils'
import AvatarComponent from '../../avatar/AvatarComponent'
import VacationBar from '../../VacationBar/VacationBar'

export interface VacationTableRowProps {
  empData: EmployeeVacation[]
  selectedDate: dayjs.Dayjs
  employee: EmployeeVacation
  handleClick: (
    employee: EmployeeVacation,
    endDate: string,
    startDate: string,
    desc: string,
    vacationId: string,
    displayStart: string,
    displayEnd: string,
    createdAt: string,
    groupIds: string[]
  ) => () => void
  nonVacClick: (employee: EmployeeVacation, date: string) => (e: React.MouseEvent<HTMLTableCellElement>) => void
  rowIndex: number
  chosenDate: string | null
  handleKeyDown: (e: React.KeyboardEvent<HTMLTableCellElement>) => void
  cellRefs: React.MutableRefObject<React.RefObject<HTMLTableCellElement>[][]>
  acknowledgementData?: { [vacationId: string]: { acknowledgements: VacationAcknowledgement[] } }
  isFetchingAcknowledgements?: boolean
  employeeLeadersMap?: Record<string, string[]>
}

const clipToMonth = (vacationStart: string, vacationEnd: string, month: dayjs.Dayjs) => {
  const clippedStart = dayjs(vacationStart).isBefore(month.startOf('month'))
    ? month.startOf('month')
    : dayjs(vacationStart)
  const clippedEnd = dayjs(vacationEnd).isAfter(month.endOf('month')) ? month.endOf('month') : dayjs(vacationEnd)
  return clippedEnd.isBefore(clippedStart) ? null : { start: clippedStart, end: clippedEnd }
}

const mergeVacations = (vacations: Vacation[]) => {
  const sortedVacations = [...vacations].sort((a, b) => dayjs(a.startDate).diff(dayjs(b.startDate)))
  const mergedResult: (Vacation & { groupIds: string[] })[] = []
  sortedVacations.forEach(currentVacation => {
    if (!mergedResult.length) {
      mergedResult.push({ ...currentVacation, groupIds: [currentVacation.vacationId] })
      return
    }
    const lastMerged = mergedResult[mergedResult.length - 1]
    if (
      dayjs(currentVacation.startDate).isAfter(dayjs(lastMerged.endDate)) ||
      dayjs(currentVacation.endDate).isBefore(dayjs(lastMerged.startDate))
    ) {
      mergedResult.push({ ...currentVacation, groupIds: [currentVacation.vacationId] })
    } else {
      lastMerged.endDate = dayjs(currentVacation.endDate).isAfter(dayjs(lastMerged.endDate))
        ? currentVacation.endDate
        : lastMerged.endDate
      lastMerged.displayEndDate = dayjs(currentVacation.displayEndDate).isAfter(dayjs(lastMerged.displayEndDate))
        ? currentVacation.displayEndDate
        : lastMerged.displayEndDate
      const descriptions = [lastMerged.description, currentVacation.description].filter(
        desc => desc && desc.trim() !== ''
      )
      lastMerged.description = Array.from(new Set(descriptions)).join(', ')
      lastMerged.groupIds = Array.from(new Set([...lastMerged.groupIds, currentVacation.vacationId]))
    }
  })
  return mergedResult
}

const VacationTableRow: React.FC<VacationTableRowProps> = ({
  empData,
  selectedDate,
  employee,
  handleClick,
  nonVacClick,
  rowIndex,
  chosenDate,
  handleKeyDown,
  cellRefs,
  acknowledgementData,
  isFetchingAcknowledgements,
  employeeLeadersMap,
}) => {
  const theme = useTheme()
  const isDownXsMid = useMediaQuery(theme.breakpoints.down('xsMid'))
  const { t } = useTranslation()
  const navigate = useNavigate()
  const currentUser = useSelector(getCurrentUser)
  const isLeaderOrManagement = useSelector(getIsLeaderOrManagement)
  const isLeader = useSelector(getIsLeader)
  const isManagement = useSelector(getIsManagement)
  const isAdmin = useSelector(getIsAdmin)
  const isSelf = currentUser?.id === employee.id
  const isCurrentUserLeaderOfEmployee =
    isLeader && !isSelf && !!employeeLeadersMap?.[employee.id]?.includes(currentUser?.id ?? '')

  const daysOfMonth = useMemo(() => getDaysOfTheMonth(selectedDate), [selectedDate])
  const mergedVacations = useMemo(() => mergeVacations(employee.vacation), [employee.vacation])

  const barMap: { [startDayIndex: number]: { vacation: Vacation & { groupIds: string[] }; length: number } } =
    useMemo(() => {
      const map: { [startDayIndex: number]: { vacation: Vacation & { groupIds: string[] }; length: number } } = {}
      mergedVacations.forEach(vacationItem => {
        const clipped = clipToMonth(vacationItem.startDate, vacationItem.endDate, selectedDate)
        if (!clipped) return
        const startDayIndex = clipped.start.date() - 1
        map[startDayIndex] = { vacation: vacationItem, length: clipped.end.diff(clipped.start, 'day') + 1 }
      })
      return map
    }, [mergedVacations, selectedDate])

  const getLeaderAcknowledgedVacation = useCallback(
    (vacationId: string): boolean | undefined => {
      const canAcknowledge = (isCurrentUserLeaderOfEmployee || isAdmin || isManagement) && !isSelf
      if (!canAcknowledge) return undefined
      const ackData = acknowledgementData
      if (isFetchingAcknowledgements || !ackData) return false
      return Boolean(
        ackData[vacationId] &&
          ackData[vacationId].acknowledgements.some((ack: VacationAcknowledgement) => {
            return ack.acknowledgedBy === currentUser?.id
          })
      )
    },
    [
      acknowledgementData,
      isCurrentUserLeaderOfEmployee,
      isAdmin,
      isManagement,
      isSelf,
      isFetchingAcknowledgements,
      currentUser?.id,
    ]
  )

  const renderEmployeeCell = () => {
    const names = employee.name.split(' ')
    const shownName = names.length > 1 && names[0] !== names[names.length - 1] ? employee.name : names[0]

    return (
      <TableCell
        sx={{
          width: 'fit-content',
          borderRight: '1px solid',
          borderRightColor: 'background.elevation8',
          textAlign: 'center',
          p: 1.5,
          position: 'sticky',
          left: 0,
          bgcolor: 'background.main',
          zIndex: 4,
        }}
      >
        <Tooltip title={shownName.length > 13 || isDownXsMid ? shownName : ''} arrow>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ cursor: isLeaderOrManagement || isSelf ? 'pointer' : 'auto' }}
            onClick={() => {
              if (isLeaderOrManagement || isSelf) {
                currentUser?.id !== employee.id
                  ? navigate(generatePath(navigation.userProfile, { id: employee.id }))
                  : navigate(navigation.myProfile)
              }
            }}
            onKeyDown={e => {
              if ((e.key === 'Enter' || e.key === ' ') && (isLeaderOrManagement || isSelf)) {
                e.preventDefault()
                currentUser?.id !== employee.id
                  ? navigate(generatePath(navigation.userProfile, { id: employee.id }))
                  : navigate(navigation.myProfile)
              }
            }}
            tabIndex={isLeaderOrManagement || isSelf ? 0 : -1}
          >
            <AvatarComponent avatar={employee.avatar} extension={employee.extension} alt={employee.name} size={24} />
            {!isDownXsMid && (
              <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
                {shownName.length > 13 ? `${shownName.slice(0, 13)}…` : shownName}
              </Typography>
            )}
          </Stack>
        </Tooltip>
      </TableCell>
    )
  }

  const renderDayCells = () => {
    const cells: React.ReactNode[] = []
    for (let col = 0; col < daysOfMonth.length; col++) {
      if (barMap[col]) {
        const { vacation, length } = barMap[col]
        const startIso = dayjs(vacation.startDate).isBefore(selectedDate.startOf('month'))
          ? selectedDate.startOf('month').format('YYYY-MM-DD')
          : vacation.startDate
        cells.push(
          <TableCell
            id={`${rowIndex}-${col}`}
            ref={cellRefs.current[rowIndex][col]}
            key={`vac-${col}`}
            colSpan={length}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
            onClick={() =>
              handleClick(
                employee,
                vacation.endDate,
                vacation.startDate,
                vacation.description,
                vacation.vacationId,
                vacation.displayStartDate,
                vacation.displayEndDate,
                vacation.createdAt,
                vacation.groupIds
              )()
            }
            sx={{
              px: 0.5,
              borderBottom: rowIndex === empData.length - 1 ? 0 : '1px solid',
              borderBottomColor: 'background.elevation8',
              cursor: isManagement || isSelf ? 'pointer' : 'auto',
              position: 'relative',
              zIndex: 1,
              ':hover': {
                backgroundColor: 'background.elevation2',
                borderRadius: 0.5,
              },
            }}
          >
            <VacationBar
              vacation={vacation}
              cellDate={startIso}
              isSingleDay={vacation.startDate === vacation.endDate}
              chosenDate={chosenDate}
              currentUser={currentUser}
              employeeName={employee.name}
              theme={theme}
              t={t}
              isLeader={isCurrentUserLeaderOfEmployee}
              leaderAcknowledgedVacation={getLeaderAcknowledgedVacation(vacation.vacationId)}
            />
          </TableCell>
        )
        col += length - 1
      } else {
        const isoDate = daysOfMonth[col].date.format('YYYY-MM-DD')
        cells.push(
          <TableCell
            id={`${rowIndex}-${col}`}
            ref={cellRefs.current[rowIndex][col]}
            key={`empty-${col}`}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
            onClick={e => nonVacClick(employee, isoDate)(e)}
            sx={{
              px: 0.5,
              borderBottom: rowIndex === empData.length - 1 ? 0 : '1px solid',
              borderBottomColor: 'background.elevation8',
              cursor: isManagement || isSelf ? 'pointer' : 'auto',
              position: 'relative',
              zIndex: 1,
              ':hover': {
                backgroundColor: 'background.elevation2',
                borderRadius: 0.5,
              },
            }}
          />
        )
      }
    }
    return cells
  }

  return (
    <>
      {renderEmployeeCell()}
      {renderDayCells()}
    </>
  )
}

export default React.memo(VacationTableRow)
